// LQC-2370 - How unwind (NPV / Recoupon) blocks interact with the trade's
// Side (quoteType).
//
// THE RULE, in both directions:
//   NPV is mutually exclusive with Two-Way.
//     - Forward: a Two-Way side disables the NPV checkboxes
//       (isTwoWayQuoteType / isBlockDisabledForNPV in UnwindHeaderCheckboxHelpers).
//     - Reverse: an NPV block forces Side to One-Way + Direction SHOW and locks
//       the Side control (setTradeSettingsToOneWayDirectionShown).
//   Recoupon is free in both directions.
//     Recoupon is actioned as part of the execute workflow, by which point the
//     trade is at Awarded and the direction/side has already been released, so
//     Two-Way + Recoupon is a valid combination.
//
// Recoupon DID force One-Way from Nov 2025 (#14420) until LQC-2370. That was
// never intentional: the forward guard was always NPV-only, so the two halves
// disagreed. Do not re-add Recoupon to the Side lock.
//
// This module is the single source of truth for detecting each unwind type,
// reused by CPSettingsGrid, SelectPanelSettings and SelectPanelUnwindIndicator.

import { IBlockAssigned } from "@/store/tradeBlockDataStore";

// An NPV block locks Side to One-Way (see THE RULE above).
export function blocksContainNPV(blocks: IBlockAssigned[] | undefined): boolean {
  return (blocks ?? []).some((block) => block.unwind === "NPV");
}

// Recoupon does NOT lock Side. Drives the green tick indicator only.
export function blocksContainRecoupon(
  blocks: IBlockAssigned[] | undefined,
): boolean {
  return (blocks ?? []).some((block) => block.unwind === "Recoupon");
}

// LQC-2271 - a Close leg forces the trade to NPV (see isBlockDisabledForNPV),
// which in turn locks Side to One-Way. Read off the trade's legs so the Select
// Panel and the Trade Settings sidebar detect it the same way.
// `openClose` is typed as a numeric enum on the trade interface but arrives as
// the string "Close" from the API, so it is read loosely here.
export function tradeContainsCloseLeg(
  trade: { legs?: { openClose?: unknown }[] } | undefined,
): boolean {
  return (trade?.legs ?? []).some((leg) => leg?.openClose === "Close");
}

// LQC-2271 - every reason Side is frozen at One-Way, in one place. Orders are
// One-Way by contract (the backend rejects Two-Way); Close and NPV are the
// unwind rules above.
export function isSideLockedToOneWay({
  containsClose,
  containsNPV,
  isOrder,
}: {
  containsClose: boolean;
  containsNPV: boolean;
  isOrder: boolean;
}): boolean {
  return containsClose || containsNPV || isOrder;
}

// Why Side is locked, for the sidebar's hover tooltip. Order matches
// isSideLockedToOneWay's precedence; empty string when nothing locks it.
export function sideLockReason({
  containsClose,
  containsNPV,
  isOrder,
}: {
  containsClose: boolean;
  containsNPV: boolean;
  isOrder: boolean;
}): string {
  if (containsClose) return "Trade contains a Close leg - Side is One-Way.";
  if (containsNPV) return "Trade contains an NPV block - Side is One-Way.";
  if (isOrder) return "Orders are always One-Way.";
  return "";
}
